// ============================================================================
// USE CUSTOMER PRICING HOOK
// React hook for customer pricing operations
// Date: June 2026
// ============================================================================

import { useState, useCallback } from 'react';
import { authService } from '@/lib/authService';
import type {
  CustomerPriceList,
  CustomerItemPrice,
  PricingContext,
} from '@/lib/inventory/customerPricingService';

export function useCustomerPricing() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get active price lists for a customer
   */
  const fetchCustomerPriceLists = useCallback(async (input: {
    client_id: number;
    contact_id: number;
    include_inactive?: boolean;
  }): Promise<CustomerPriceList[]> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getCustomerPriceLists',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch customer price lists');
      }

      return result.data as CustomerPriceList[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch customer price lists';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get default price list for a customer
   */
  const fetchCustomerDefaultPriceList = useCallback(async (input: {
    client_id: number;
    contact_id: number;
  }): Promise<CustomerPriceList | null> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getCustomerDefaultPriceList',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch default price list');
      }

      return result.data as CustomerPriceList | null;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch default price list';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get customer-specific price for a single item
   */
  const fetchCustomerItemPrice = useCallback(async (input: {
    client_id: number;
    contact_id: number;
    work_item_id: string;
    quantity?: number | null;
    date?: string | null;
  }): Promise<CustomerItemPrice | null> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getCustomerItemPrice',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch customer item price');
      }

      return result.data as CustomerItemPrice | null;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch customer item price';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get customer-specific prices for multiple items
   */
  const fetchCustomerItemPrices = useCallback(async (input: {
    client_id: number;
    contact_id: number;
    work_item_ids: string[];
    quantity?: number | null;
    date?: string | null;
  }): Promise<Map<string, CustomerItemPrice>> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getCustomerItemPrices',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch customer item prices');
      }

      // Convert object back to Map
      return new Map(Object.entries(result.data as Record<string, CustomerItemPrice>));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch customer item prices';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get base pricing for items (fallback when no customer-specific pricing)
   */
  const fetchBaseItemPricing = useCallback(async (input: {
    client_id: number;
    work_item_ids: string[];
  }): Promise<Map<string, { base_price: number; currency: string }>> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getBaseItemPricing',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch base item pricing');
      }

      // Convert object back to Map
      return new Map(Object.entries(result.data as Record<string, { base_price: number; currency: string }>));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch base item pricing';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get best available price (customer-specific or base)
   */
  const fetchBestAvailablePrice = useCallback(async (input: {
    client_id: number;
    contact_id: number;
    work_item_id: string;
    quantity?: number | null;
    date?: string | null;
  }): Promise<{ price: number; currency: string; source: 'customer' | 'base' }> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/customer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getBestAvailablePrice',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch best available price');
      }

      return result.data as { price: number; currency: string; source: 'customer' | 'base' };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch best available price';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Auto-apply customer pricing to line items
   * This is a convenience function that fetches pricing for all items and returns a map
   */
  const autoApplyCustomerPricing = useCallback(async (input: {
    client_id: number;
    contact_id: number;
    lineItems: Array<{ work_item_id: string; quantity: number; current_price?: number }>;
    date?: string | null;
  }): Promise<Array<{
    work_item_id: string;
    applied_price: number;
    currency: string;
    source: 'customer' | 'base' | 'unchanged';
    price_list_name?: string;
    discount_percentage?: number | null;
  }>> => {
    setLoading(true);
    setError(null);
    try {
      const { client_id, contact_id, lineItems, date } = input;
      const workItemIds = lineItems.map(item => item.work_item_id);

      // Fetch customer-specific pricing for all items
      const customerPrices = await fetchCustomerItemPrices({
        client_id,
        contact_id,
        work_item_ids: workItemIds,
        date,
      });

      // Fetch base pricing as fallback
      const basePrices = await fetchBaseItemPricing({
        client_id,
        work_item_ids: workItemIds,
      });

      // Apply pricing to each line item
      const results = lineItems.map(lineItem => {
        const customerPrice = customerPrices.get(lineItem.work_item_id);
        const basePrice = basePrices.get(lineItem.work_item_id);

        if (customerPrice) {
          return {
            work_item_id: lineItem.work_item_id,
            applied_price: customerPrice.final_price,
            currency: customerPrice.currency,
            source: 'customer' as const,
            price_list_name: customerPrice.price_list_name,
            discount_percentage: customerPrice.discount_percentage,
          };
        } else if (basePrice) {
          return {
            work_item_id: lineItem.work_item_id,
            applied_price: basePrice.base_price,
            currency: basePrice.currency,
            source: 'base' as const,
          };
        } else {
          // Keep current price if no pricing found
          return {
            work_item_id: lineItem.work_item_id,
            applied_price: lineItem.current_price || 0,
            currency: 'ZAR',
            source: 'unchanged' as const,
          };
        }
      });

      return results;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to auto-apply customer pricing';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchCustomerItemPrices, fetchBaseItemPricing]);

  return {
    loading,
    error,
    clearError,
    fetchCustomerPriceLists,
    fetchCustomerDefaultPriceList,
    fetchCustomerItemPrice,
    fetchCustomerItemPrices,
    fetchBaseItemPricing,
    fetchBestAvailablePrice,
    autoApplyCustomerPricing,
  };
}